import { useId } from 'react';
import type {
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';

interface FieldChrome {
  label: string;
  hint?: string;
  error?: string;
  required?: boolean;
}

const CONTROL_CLASSES =
  'w-full rounded-lg border bg-surface text-text-primary text-sm px-3 py-2 ' +
  'placeholder:text-text-muted transition-colors duration-150 ' +
  'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background focus-visible:outline-none ' +
  'disabled:bg-bg-subtle disabled:text-text-muted disabled:cursor-not-allowed';

function FieldShell({
  id,
  label,
  hint,
  error,
  required,
  children,
}: FieldChrome & { id: string; children: ReactNode }) {
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="block text-xs font-medium text-text-secondary">
        {label}
        {required && (
          <span className="text-error ml-0.5" aria-hidden="true">
            *
          </span>
        )}
      </label>
      {children}
      {error ? (
        <p id={`${id}-error`} role="alert" className="text-xs text-error">
          {error}
        </p>
      ) : hint ? (
        <p id={`${id}-hint`} className="text-xs text-text-muted">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

function describedBy(id: string, error?: string, hint?: string): string | undefined {
  if (error) return `${id}-error`;
  if (hint) return `${id}-hint`;
  return undefined;
}

export function Input({
  label,
  hint,
  error,
  required,
  className = '',
  id: givenId,
  ...rest
}: FieldChrome & InputHTMLAttributes<HTMLInputElement>) {
  const autoId = useId();
  const id = givenId ?? autoId;
  return (
    <FieldShell
      id={id}
      label={label}
      {...(hint ? { hint } : {})}
      {...(error ? { error } : {})}
      {...(required ? { required } : {})}
    >
      <input
        id={id}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(id, error, hint)}
        className={`${CONTROL_CLASSES} ${error ? 'border-error' : 'border-border'} ${className}`}
        {...rest}
      />
    </FieldShell>
  );
}

export function Textarea({
  label,
  hint,
  error,
  required,
  className = '',
  id: givenId,
  ...rest
}: FieldChrome & TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const autoId = useId();
  const id = givenId ?? autoId;
  return (
    <FieldShell
      id={id}
      label={label}
      {...(hint ? { hint } : {})}
      {...(error ? { error } : {})}
      {...(required ? { required } : {})}
    >
      <textarea
        id={id}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(id, error, hint)}
        className={`${CONTROL_CLASSES} min-h-20 ${error ? 'border-error' : 'border-border'} ${className}`}
        {...rest}
      />
    </FieldShell>
  );
}

export function Select({
  label,
  hint,
  error,
  required,
  className = '',
  id: givenId,
  children,
  ...rest
}: FieldChrome & SelectHTMLAttributes<HTMLSelectElement>) {
  const autoId = useId();
  const id = givenId ?? autoId;
  return (
    <FieldShell
      id={id}
      label={label}
      {...(hint ? { hint } : {})}
      {...(error ? { error } : {})}
      {...(required ? { required } : {})}
    >
      <select
        id={id}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(id, error, hint)}
        className={`${CONTROL_CLASSES} ${error ? 'border-error' : 'border-border'} ${className}`}
        {...rest}
      >
        {children}
      </select>
    </FieldShell>
  );
}
